import type { CSSProperties } from "react";

/**
 * Dashboard themes.
 *
 * The store dashboard used to be hard-coded to white / emerald-50 surfaces,
 * which reads as a wall of glare on a bright POS monitor. Everything visual now
 * comes from one of the presets below, reaches the widgets as CSS custom
 * properties, and the chosen preset is saved per store (through the CMS entity
 * below, the same way screen backgrounds are stored).
 *
 * A preset carries two things:
 *   - a palette (per light/dark mode), and
 *   - a style: panel fill, button treatment, border weight, corners and
 *     typography, which is what lets a preset look genuinely different rather
 *     than merely recoloured.
 *
 * Layout is deliberately NOT themeable — every preset renders the same grid,
 * the same panels in the same order at the same proportions.
 */

export interface DashboardPalette {
  /** page background behind the panels */
  pageBg: string;
  /** panel / card background (gradient start) */
  panelBg: string;
  /** panel / card gradient end */
  panelBgAlt: string;
  panelBorder: string;
  /** panel titles */
  heading: string;
  /** metric labels, secondary text */
  muted: string;
  /** metric values */
  value: string;
  /** metric icon chip, "up" trend */
  iconBg: string;
  iconText: string;
  /** metric icon chip, "down" trend */
  iconBgDown: string;
  iconTextDown: string;
  /** quick action buttons */
  actionEmerald: string;
  actionBlue: string;
  actionRed: string;
  /** label colour on a solid button; unused when buttons are outlined */
  actionText: string;
  /** "Tickets on Screen" amount tiles */
  amountBg: string;
  amountText: string;
  /** ticket type tiles */
  typeNewBg: string;
  typeNewText: string;
  typeLuckyBg: string;
  typeLuckyText: string;
  typeEndingBg: string;
  typeEndingText: string;
}

/**
 * The non-colour half of a theme. This is what separates "the same dashboard in
 * another colour" from "a different-looking dashboard".
 */
export interface DashboardStyle {
  /** panel / card background treatment */
  panelFill: "gradient" | "solid" | "none";
  /** solid buttons, or transparent with a coloured rule and matching label */
  buttonStyle: "solid" | "outline";
  /** same choice for the amount / ticket-type tiles */
  tileStyle: "solid" | "outline";
  /** border weight on panels, buttons and tiles, px */
  borderWidth: number;
  /** uppercase the panel titles and metric labels */
  labelCase: "none" | "upper";
  /** letter-spacing for those labels, em */
  labelTracking: number;
  /** metric values in the UI face or a monospaced one */
  numericFont: "sans" | "mono";
  headingWeight: number;
  /** coloured glow instead of a drop shadow */
  glow: string | null;
}

/**
 * A palette is described by its surfaces, text and four accent pairs; the 21
 * widget colours are derived from those so every preset stays internally
 * consistent (the "lucky" tile and the "up" metric chip are always the same
 * green, and so on).
 */
interface PaletteSeed {
  pageBg: string;
  panelBg: string;
  panelBgAlt: string;
  panelBorder: string;
  heading: string;
  muted: string;
  value: string;
  greenBg: string;
  greenText: string;
  redBg: string;
  redText: string;
  blueBg: string;
  blueText: string;
  amberBg: string;
  amberText: string;
  actionEmerald: string;
  actionBlue: string;
  actionRed: string;
  actionText: string;
}

const buildPalette = (s: PaletteSeed): DashboardPalette => ({
  pageBg: s.pageBg,
  panelBg: s.panelBg,
  panelBgAlt: s.panelBgAlt,
  panelBorder: s.panelBorder,
  heading: s.heading,
  muted: s.muted,
  value: s.value,
  iconBg: s.greenBg,
  iconText: s.greenText,
  iconBgDown: s.redBg,
  iconTextDown: s.redText,
  actionEmerald: s.actionEmerald,
  actionBlue: s.actionBlue,
  actionRed: s.actionRed,
  actionText: s.actionText,
  amountBg: s.greenBg,
  amountText: s.greenText,
  typeNewBg: s.blueBg,
  typeNewText: s.blueText,
  typeLuckyBg: s.greenBg,
  typeLuckyText: s.greenText,
  typeEndingBg: s.amberBg,
  typeEndingText: s.amberText,
});

/** The understated treatment the colour-led presets share. */
const CALM_STYLE: DashboardStyle = {
  panelFill: "gradient",
  buttonStyle: "solid",
  tileStyle: "solid",
  borderWidth: 1,
  labelCase: "none",
  labelTracking: 0,
  numericFont: "sans",
  headingWeight: 600,
  glow: null,
};

/* ------------------------------- palettes ------------------------------- */

/** Muted grey-blue. The default: light, without the white glare. */
const SOFT_LIGHT = buildPalette({
  pageBg: "#e7e9ee",
  panelBg: "#dfe3ea",
  panelBgAlt: "#d8dde6",
  panelBorder: "#c3c9d4",
  heading: "#1f2937",
  muted: "#5b6472",
  value: "#111827",
  greenBg: "#cfe1d9",
  greenText: "#1f5540",
  redBg: "#e8cfd0",
  redText: "#8f2b2e",
  blueBg: "#d6dfee",
  blueText: "#26456f",
  amberBg: "#ebe0c8",
  amberText: "#6d5312",
  // Action colours clear 4.5:1 against actionText — the quick action labels are
  // small and read at arm's length on a POS monitor.
  actionEmerald: "#26775b",
  actionBlue: "#3b6fb0",
  actionRed: "#b8474a",
  actionText: "#f4f6f8",
});

/** Cooler and a shade deeper than Soft Neutral. */
const SLATE_LIGHT = buildPalette({
  pageBg: "#d8dee7",
  panelBg: "#ced6e1",
  panelBgAlt: "#c4cdda",
  panelBorder: "#aeb9c9",
  heading: "#1b2432",
  muted: "#4f596a",
  value: "#0f172a",
  greenBg: "#bfd3ca",
  greenText: "#18493a",
  redBg: "#e0c6c7",
  redText: "#872a2d",
  blueBg: "#c7d4e8",
  blueText: "#1f3c63",
  amberBg: "#e2d6bb",
  amberText: "#5f480f",
  actionEmerald: "#26775b",
  actionBlue: "#35659f",
  actionRed: "#ad4245",
  actionText: "#f4f6f8",
});

/** Warm beige — the least clinical of the light options. */
const SAND_LIGHT = buildPalette({
  pageBg: "#ece7e0",
  panelBg: "#e4ded5",
  panelBgAlt: "#ddd6cb",
  panelBorder: "#cbc2b4",
  heading: "#2b2620",
  muted: "#665d50",
  value: "#1c1813",
  greenBg: "#d5dfc9",
  greenText: "#3a5127",
  redBg: "#ecd3cb",
  redText: "#8a3420",
  blueBg: "#d5dde8",
  blueText: "#2c4460",
  amberBg: "#edddc0",
  amberText: "#664c0e",
  actionEmerald: "#4f7139",
  actionBlue: "#40658d",
  actionRed: "#a44a35",
  actionText: "#faf7f2",
});

/** Muted green-grey, easy on the eyes over a long shift. */
const SAGE_LIGHT = buildPalette({
  pageBg: "#e3e8e2",
  panelBg: "#dae1d8",
  panelBgAlt: "#d2dace",
  panelBorder: "#bcc7b9",
  heading: "#1f2a23",
  muted: "#515e56",
  value: "#131c16",
  greenBg: "#c9dcc6",
  greenText: "#24512f",
  redBg: "#e6cfcc",
  redText: "#872f2b",
  blueBg: "#d1dce4",
  blueText: "#24475c",
  amberBg: "#e6dfc0",
  amberText: "#5e4d0f",
  actionEmerald: "#2f6b45",
  actionBlue: "#3a6485",
  actionRed: "#a04642",
  actionText: "#f5f8f4",
});

/**
 * The original look, kept so a store can go back to it. Deliberately reproduces
 * the old emerald-500 buttons, whose white label lands at 3.8:1 rather than the
 * 4.5:1 every other preset clears — fidelity to the previous design is the point
 * of this one.
 */
const CLASSIC_LIGHT = buildPalette({
  pageBg: "#f7f8fa",
  panelBg: "#ecfdf5",
  panelBgAlt: "#d1fae5",
  panelBorder: "#a7f3d0",
  heading: "#111827",
  muted: "#4b5563",
  value: "#111827",
  greenBg: "#d1fae5",
  greenText: "#047857",
  redBg: "#fee2e2",
  redText: "#b91c1c",
  blueBg: "#dbeafe",
  blueText: "#1d4ed8",
  amberBg: "#fef9c3",
  amberText: "#a16207",
  actionEmerald: "#059669",
  actionBlue: "#2563eb",
  actionRed: "#dc2626",
  actionText: "#ffffff",
});

/** Neutral dark grey — matches the gray-800/900 tones used elsewhere. */
const CHARCOAL_DARK = buildPalette({
  pageBg: "#111827",
  panelBg: "#1f2937",
  panelBgAlt: "#1b2431",
  panelBorder: "#374151",
  heading: "#f9fafb",
  muted: "#9ca3af",
  value: "#ffffff",
  greenBg: "#064e3b",
  greenText: "#6ee7b7",
  redBg: "#7f1d1d",
  redText: "#fca5a5",
  blueBg: "#17304d",
  blueText: "#93c5fd",
  amberBg: "#3f3417",
  amberText: "#fcd34d",
  actionEmerald: "#047857",
  actionBlue: "#2563eb",
  actionRed: "#dc2626",
  actionText: "#f9fafb",
});

/** Deep navy. */
const MIDNIGHT_DARK = buildPalette({
  pageBg: "#0f172a",
  panelBg: "#1b2536",
  panelBgAlt: "#16202f",
  panelBorder: "#2c3a51",
  heading: "#f8fafc",
  muted: "#94a3b8",
  value: "#ffffff",
  greenBg: "#064e3b",
  greenText: "#6ee7b7",
  redBg: "#7f1d1d",
  redText: "#fca5a5",
  blueBg: "#1a3358",
  blueText: "#93c5fd",
  amberBg: "#3f3417",
  amberText: "#fcd34d",
  actionEmerald: "#047857",
  actionBlue: "#2563eb",
  actionRed: "#dc2626",
  actionText: "#f9fafb",
});

/** Dark with a green cast. */
const FOREST_DARK = buildPalette({
  pageBg: "#0e1a14",
  panelBg: "#16261d",
  panelBgAlt: "#12201a",
  panelBorder: "#26402f",
  heading: "#f2f7f3",
  muted: "#93a89a",
  value: "#ffffff",
  greenBg: "#0c4430",
  greenText: "#7ee0ae",
  redBg: "#4d1f1c",
  redText: "#f5a8a0",
  blueBg: "#16304a",
  blueText: "#8fc4f0",
  amberBg: "#3b3416",
  amberText: "#f2ce6a",
  actionEmerald: "#16794f",
  actionBlue: "#2f6bb5",
  actionRed: "#b3382f",
  actionText: "#f4faf6",
});

/* ---------------------------- statement themes --------------------------- */

/** Pure black, white rules, nothing filled. */
const MONO = buildPalette({
  pageBg: "#000000",
  panelBg: "#000000",
  panelBgAlt: "#000000",
  panelBorder: "#ffffff",
  heading: "#ffffff",
  muted: "#c8c8c8",
  value: "#ffffff",
  greenBg: "#000000",
  greenText: "#ffffff",
  redBg: "#000000",
  redText: "#ff9b9b",
  blueBg: "#000000",
  blueText: "#ffffff",
  amberBg: "#000000",
  amberText: "#ffffff",
  actionEmerald: "#ffffff",
  actionBlue: "#ffffff",
  actionRed: "#ff9b9b",
  actionText: "#000000",
});

/** Near-black with saturated accents and a glow. */
const NEON = buildPalette({
  pageBg: "#07080d",
  panelBg: "#0d1018",
  panelBgAlt: "#0d1018",
  panelBorder: "#2de2e6",
  heading: "#eafcff",
  muted: "#93aebb",
  value: "#ffffff",
  greenBg: "#06231d",
  greenText: "#4dfacb",
  redBg: "#2a0d18",
  redText: "#ff6ea9",
  blueBg: "#0a1e33",
  blueText: "#6cc0ff",
  amberBg: "#2b210a",
  amberText: "#ffd166",
  actionEmerald: "#2de2e6",
  actionBlue: "#a855f7",
  actionRed: "#ff2e88",
  actionText: "#06070d",
});

/** Drafting-table navy, hairline rules, everything outlined. */
const BLUEPRINT = buildPalette({
  pageBg: "#0a1b2e",
  panelBg: "#0a1b2e",
  panelBgAlt: "#0a1b2e",
  panelBorder: "#7fb4e0",
  heading: "#e8f3ff",
  muted: "#a8c8e2",
  value: "#ffffff",
  greenBg: "#0a1b2e",
  greenText: "#a6e3c4",
  redBg: "#0a1b2e",
  redText: "#ffb4b4",
  blueBg: "#0a1b2e",
  blueText: "#cfe8ff",
  amberBg: "#0a1b2e",
  amberText: "#ffe1a3",
  actionEmerald: "#cfe8ff",
  actionBlue: "#cfe8ff",
  actionRed: "#ffb4b4",
  actionText: "#0a1b2e",
});

/** Maximum legibility across the shop floor: black, yellow, square. */
const SIGNAL = buildPalette({
  pageBg: "#000000",
  panelBg: "#101010",
  panelBgAlt: "#101010",
  panelBorder: "#ffd400",
  heading: "#ffffff",
  muted: "#e6e6e6",
  value: "#ffffff",
  greenBg: "#ffd400",
  greenText: "#000000",
  redBg: "#ff3b30",
  redText: "#000000",
  blueBg: "#ffffff",
  blueText: "#000000",
  amberBg: "#ffd400",
  amberText: "#000000",
  actionEmerald: "#ffd400",
  actionBlue: "#ffffff",
  actionRed: "#ff3b30",
  actionText: "#000000",
});

/** Ink on paper: light, but flat and hard-edged rather than glossy. */
const PRESS = buildPalette({
  pageBg: "#efece4",
  panelBg: "#efece4",
  panelBgAlt: "#efece4",
  panelBorder: "#221f1a",
  heading: "#17150f",
  muted: "#4d473c",
  value: "#100e0a",
  greenBg: "#efece4",
  greenText: "#1f4d33",
  redBg: "#efece4",
  redText: "#8a2b1f",
  blueBg: "#efece4",
  blueText: "#24405e",
  amberBg: "#efece4",
  amberText: "#6b4c0d",
  actionEmerald: "#221f1a",
  actionBlue: "#35506e",
  actionRed: "#8a2b1f",
  actionText: "#f5f2ea",
});

/* ------------------------- easy-on-the-eyes themes ------------------------ */

/**
 * Built for older staff working long shifts.
 *
 * Age-related vision changes are specific, so these are too: reduced contrast
 * sensitivity means text targets AAA (7:1) rather than AA; presbyopia means
 * larger type; smaller pupils need light surfaces, but increased glare
 * sensitivity rules out white, so backgrounds are soft and off-white; the lens
 * yellows with age and blue discrimination fades, so nothing important is
 * carried by blue alone; and 2px rules keep panels clearly separated.
 *
 * Labels stay sentence case here — uppercase strips the word shapes readers
 * rely on and measurably slows reading, which is the opposite of the point.
 */
const EASY_STYLE: DashboardStyle = {
  panelFill: "solid",
  buttonStyle: "solid",
  tileStyle: "solid",
  borderWidth: 2,
  labelCase: "none",
  labelTracking: 0.01,
  numericFont: "sans",
  headingWeight: 700,
  glow: null,
};

/** Soft warm ivory, near-black text. The flagship of the group. */
const CLEAR_DAY = buildPalette({
  pageBg: "#e6e1d6",
  panelBg: "#f3f0e9",
  panelBgAlt: "#eeeae1",
  panelBorder: "#6f685c",
  heading: "#14120e",
  muted: "#3b3630",
  value: "#0d0b08",
  greenBg: "#cfe0c4",
  greenText: "#16330f",
  redBg: "#f2d2ca",
  redText: "#5f1a0c",
  blueBg: "#d3dcea",
  blueText: "#152b47",
  amberBg: "#f1e0b9",
  amberText: "#4a3304",
  actionEmerald: "#26521c",
  actionBlue: "#264263",
  actionRed: "#7a2614",
  actionText: "#fbf9f5",
});

/** Warm amber and brown — the least blue-dependent option. */
const GOLDEN_HOUR = buildPalette({
  pageBg: "#e9dcc6",
  panelBg: "#f6eeda",
  panelBgAlt: "#f0e7d1",
  panelBorder: "#77664a",
  heading: "#1a1509",
  muted: "#413621",
  value: "#120e05",
  greenBg: "#d7e2be",
  greenText: "#26370d",
  redBg: "#f4d3c1",
  redText: "#65200a",
  blueBg: "#dbdfe9",
  blueText: "#1d2942",
  amberBg: "#f7e0a8",
  amberText: "#503400",
  actionEmerald: "#3f4f18",
  actionBlue: "#2b4460",
  actionRed: "#7a2a0e",
  actionText: "#fdf8ee",
});

/** Warm dark rather than black — easier on the eyes for evening shifts. */
const SOFT_DUSK = buildPalette({
  pageBg: "#1b1917",
  panelBg: "#272320",
  panelBgAlt: "#221f1c",
  panelBorder: "#8b8275",
  heading: "#f9f5ee",
  muted: "#d5cdbe",
  value: "#ffffff",
  greenBg: "#1e3a1c",
  greenText: "#a9e2a1",
  redBg: "#4a1e14",
  redText: "#ffb6a3",
  blueBg: "#1c3048",
  blueText: "#a9cff2",
  amberBg: "#453510",
  amberText: "#f7d685",
  // High-visibility buttons: light fills with dark labels stand out against the
  // dark ground far better than dark fills would, and clear AAA on the label.
  actionEmerald: "#9ed492",
  actionBlue: "#a5c8ef",
  actionRed: "#f2a48e",
  actionText: "#12100e",
});

/** Maximum legibility: black rules, black text, the largest type. */
const LARGE_PRINT = buildPalette({
  pageBg: "#e4e4e2",
  panelBg: "#f7f7f5",
  panelBgAlt: "#f2f2f0",
  panelBorder: "#1a1a1a",
  heading: "#000000",
  muted: "#262626",
  value: "#000000",
  greenBg: "#d6ead2",
  greenText: "#123a0c",
  redBg: "#f6d6d1",
  redText: "#5f1006",
  blueBg: "#d5dff0",
  blueText: "#0e2447",
  amberBg: "#f6e4b8",
  amberText: "#453000",
  actionEmerald: "#14532d",
  actionBlue: "#10305e",
  actionRed: "#6d1206",
  actionText: "#ffffff",
});

/** Calm muted green — restful over a long shift, still high contrast. */
const GENTLE_SAGE = buildPalette({
  pageBg: "#dde5d8",
  panelBg: "#eef2ea",
  panelBgAlt: "#e8ede3",
  panelBorder: "#68735f",
  heading: "#101a0d",
  muted: "#333d2d",
  value: "#0b120a",
  greenBg: "#cfe2c6",
  greenText: "#15330f",
  redBg: "#f1d5ce",
  redText: "#621a0c",
  blueBg: "#d5dfe8",
  blueText: "#152c45",
  amberBg: "#efe1b9",
  amberText: "#463502",
  actionEmerald: "#26501e",
  actionBlue: "#264463",
  actionRed: "#742513",
  actionText: "#fbfdf9",
});

/* -------------------------------- presets ------------------------------- */

export interface DashboardPreset {
  label: string;
  description: string;
  /** picker grouping: colour-led light/dark, a full restyle, or accessibility */
  tone: "light" | "dark" | "statement" | "easy";
  /** panel corner radius, px */
  radius: number;
  /** spacing between panels, px */
  gap: number;
  /** 0 = flat, 3 = pronounced */
  shadow: number;
  /** multiplies every dashboard font size */
  fontScale: number;
  style: DashboardStyle;
  light: DashboardPalette;
  dark: DashboardPalette;
}

export type PresetKey =
  | "soft"
  | "slate"
  | "sand"
  | "sage"
  | "classic"
  | "charcoal"
  | "midnight"
  | "forest"
  | "mono"
  | "neon"
  | "blueprint"
  | "signal"
  | "press"
  | "clearday"
  | "golden"
  | "dusk"
  | "largeprint"
  | "sage2";

const LAYOUT = { radius: 16, gap: 12, shadow: 1, fontScale: 1 };

export const DASHBOARD_PRESETS: Record<PresetKey, DashboardPreset> = {
  soft: {
    ...LAYOUT,
    label: "Soft Neutral",
    description: "Muted grey-blue. The default — light, without the glare.",
    tone: "light",
    style: CALM_STYLE,
    light: SOFT_LIGHT,
    dark: CHARCOAL_DARK,
  },
  slate: {
    ...LAYOUT,
    label: "Slate",
    description: "Cooler and a shade deeper than Soft Neutral.",
    tone: "light",
    style: CALM_STYLE,
    light: SLATE_LIGHT,
    dark: MIDNIGHT_DARK,
  },
  sand: {
    ...LAYOUT,
    label: "Warm Sand",
    description: "Warm beige. The least clinical of the light options.",
    tone: "light",
    style: CALM_STYLE,
    light: SAND_LIGHT,
    dark: CHARCOAL_DARK,
  },
  sage: {
    ...LAYOUT,
    label: "Sage",
    description: "Muted green-grey, easy on the eyes over a long shift.",
    tone: "light",
    style: CALM_STYLE,
    light: SAGE_LIGHT,
    dark: FOREST_DARK,
  },
  classic: {
    ...LAYOUT,
    shadow: 2,
    label: "Classic White",
    description: "The original bright look, kept for going back.",
    tone: "light",
    style: CALM_STYLE,
    light: CLASSIC_LIGHT,
    dark: CHARCOAL_DARK,
  },
  charcoal: {
    ...LAYOUT,
    shadow: 2,
    label: "Charcoal",
    description: "Neutral dark grey, in light mode as well as dark.",
    tone: "dark",
    style: CALM_STYLE,
    light: CHARCOAL_DARK,
    dark: CHARCOAL_DARK,
  },
  midnight: {
    ...LAYOUT,
    shadow: 2,
    label: "Midnight",
    description: "Deep navy, in light mode as well as dark.",
    tone: "dark",
    style: CALM_STYLE,
    light: MIDNIGHT_DARK,
    dark: MIDNIGHT_DARK,
  },
  forest: {
    ...LAYOUT,
    shadow: 2,
    label: "Deep Forest",
    description: "Dark with a green cast, in light mode as well as dark.",
    tone: "dark",
    style: CALM_STYLE,
    light: FOREST_DARK,
    dark: FOREST_DARK,
  },

  mono: {
    ...LAYOUT,
    radius: 4,
    shadow: 0,
    label: "Mono Outline",
    description:
      "Black throughout. Nothing is filled — panels, buttons and tiles are white outlines with uppercase labels.",
    tone: "statement",
    style: {
      panelFill: "none",
      buttonStyle: "outline",
      tileStyle: "outline",
      borderWidth: 2,
      labelCase: "upper",
      labelTracking: 0.08,
      numericFont: "mono",
      headingWeight: 700,
      glow: null,
    },
    light: MONO,
    dark: MONO,
  },
  neon: {
    ...LAYOUT,
    radius: 12,
    shadow: 2,
    label: "Neon Grid",
    description:
      "Near-black with cyan, violet and magenta, monospaced figures and a coloured glow on every surface.",
    tone: "statement",
    style: {
      panelFill: "solid",
      buttonStyle: "solid",
      tileStyle: "solid",
      borderWidth: 1,
      labelCase: "upper",
      labelTracking: 0.1,
      numericFont: "mono",
      headingWeight: 700,
      glow: "#2de2e6",
    },
    light: NEON,
    dark: NEON,
  },
  blueprint: {
    ...LAYOUT,
    radius: 2,
    shadow: 0,
    label: "Blueprint",
    description:
      "Drafting-table navy. Hairline outlines, square corners, wide-tracked monospaced type throughout.",
    tone: "statement",
    style: {
      panelFill: "none",
      buttonStyle: "outline",
      tileStyle: "outline",
      borderWidth: 1,
      labelCase: "upper",
      labelTracking: 0.12,
      numericFont: "mono",
      headingWeight: 600,
      glow: null,
    },
    light: BLUEPRINT,
    dark: BLUEPRINT,
  },
  signal: {
    ...LAYOUT,
    radius: 0,
    shadow: 0,
    fontScale: 1.1,
    label: "Bold Signal",
    description:
      "Black, yellow and square, with oversized figures — built to be read from across the shop floor.",
    tone: "statement",
    style: {
      panelFill: "solid",
      buttonStyle: "solid",
      tileStyle: "solid",
      borderWidth: 2,
      labelCase: "upper",
      labelTracking: 0.06,
      numericFont: "sans",
      headingWeight: 800,
      glow: null,
    },
    light: SIGNAL,
    dark: SIGNAL,
  },
  press: {
    ...LAYOUT,
    radius: 0,
    shadow: 0,
    label: "Ivory Press",
    description:
      "Ink on paper. Light, but flat and hard-edged — dark rules, solid ink buttons, no gloss anywhere.",
    tone: "statement",
    style: {
      panelFill: "none",
      buttonStyle: "solid",
      tileStyle: "outline",
      borderWidth: 1,
      labelCase: "upper",
      labelTracking: 0.1,
      numericFont: "mono",
      headingWeight: 700,
      glow: null,
    },
    light: PRESS,
    dark: PRESS,
  },

  clearday: {
    ...LAYOUT,
    radius: 12,
    gap: 14,
    fontScale: 1.2,
    label: "Clear Day",
    description:
      "Soft warm ivory with near-black text and larger type. Bright enough for tired eyes without the glare of white.",
    tone: "easy",
    style: EASY_STYLE,
    light: CLEAR_DAY,
    dark: CLEAR_DAY,
  },
  golden: {
    ...LAYOUT,
    radius: 12,
    gap: 14,
    fontScale: 1.2,
    label: "Golden Hour",
    description:
      "Warm ambers and browns. Nothing important is carried by blue alone, which is the first colour range to fade with age.",
    tone: "easy",
    style: EASY_STYLE,
    light: GOLDEN_HOUR,
    dark: GOLDEN_HOUR,
  },
  dusk: {
    ...LAYOUT,
    radius: 12,
    gap: 14,
    fontScale: 1.2,
    label: "Soft Dusk",
    description:
      "Warm dark rather than black, with cream text — for evening shifts, and gentler than a black screen.",
    tone: "easy",
    style: EASY_STYLE,
    light: SOFT_DUSK,
    dark: SOFT_DUSK,
  },
  largeprint: {
    ...LAYOUT,
    radius: 8,
    gap: 14,
    fontScale: 1.3,
    label: "Large Print",
    description:
      "The biggest type and the strongest contrast: black text and black rules on soft paper.",
    tone: "easy",
    style: EASY_STYLE,
    light: LARGE_PRINT,
    dark: LARGE_PRINT,
  },
  sage2: {
    ...LAYOUT,
    radius: 12,
    gap: 14,
    fontScale: 1.2,
    label: "Gentle Sage",
    description:
      "A calm muted green that stays restful across a long shift while holding high contrast.",
    tone: "easy",
    style: EASY_STYLE,
    light: GENTLE_SAGE,
    dark: GENTLE_SAGE,
  },
};

/** Picker sections, in display order. Each key appears exactly once. */
export const PRESET_GROUPS: {
  title: string;
  note: string;
  keys: PresetKey[];
}[] = [
  {
    title: "Light",
    note: "Follow the app's light/dark toggle.",
    keys: ["soft", "slate", "sand", "sage", "classic"],
  },
  {
    title: "Easy on the eyes",
    note: "Larger type, stronger rules and AAA-level contrast, for staff who want a clearer screen.",
    keys: ["clearday", "golden", "dusk", "largeprint", "sage2"],
  },
  {
    title: "Dark surfaces",
    note: "Stay dark whether or not the app is in dark mode.",
    keys: ["charcoal", "midnight", "forest"],
  },
  {
    title: "Statement",
    note: "Restyle everything — fills, outlines, corners and type. Same layout.",
    keys: ["mono", "neon", "blueprint", "signal", "press"],
  },
];

export const DEFAULT_PRESET: PresetKey = "soft";

/**
 * Master switch for per-store theming.
 *
 * While false, every store renders DEFAULT_PRESET regardless of what is saved,
 * and the Appearance picker is hidden from the sidebar. Nothing else is removed
 * — the presets, the picker and the saved CMS rows all stay in place — so
 * flipping this back to true re-enables the feature exactly as it was, with
 * each store's previous choice intact.
 */
export const THEMING_ENABLED = false;

/** What gets saved per store. */
export interface DashboardThemeConfig {
  preset: PresetKey;
}

/** CMS entity the per-store choice is saved under (suffixed with the store id). */
export const DASHBOARD_THEME_ENTITY = "dim.dashboard.theme";

/** localStorage key, used to paint before the CMS request resolves. */
export const DASHBOARD_THEME_KEY = "dashboardThemeConfigV1";

/** Tolerates unknown / missing keys, so a removed preset falls back cleanly. */
export const resolvePresetKey = (key?: string | null): PresetKey =>
  key && key in DASHBOARD_PRESETS ? (key as PresetKey) : DEFAULT_PRESET;

export const paletteFor = (
  preset: DashboardPreset,
  mode: "light" | "dark",
): DashboardPalette => (mode === "dark" ? preset.dark : preset.light);

const SHADOWS = [
  "none",
  "0 1px 3px rgba(15, 23, 42, 0.10)",
  "0 4px 12px rgba(15, 23, 42, 0.14)",
  "0 10px 28px rgba(15, 23, 42, 0.22)",
];

const MONO_STACK =
  'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace';

/** The colour actually behind panel text — panels with no fill sit on the page. */
export const panelSurface = (
  preset: DashboardPreset,
  mode: "light" | "dark",
): string => {
  const p = paletteFor(preset, mode);
  return preset.style.panelFill === "none" ? p.pageBg : p.panelBg;
};

const panelFillValue = (
  preset: DashboardPreset,
  p: DashboardPalette,
): string => {
  if (preset.style.panelFill === "none") return "transparent";
  if (preset.style.panelFill === "solid") return p.panelBg;
  return `linear-gradient(to bottom right, ${p.panelBg}, ${p.panelBgAlt})`;
};

/**
 * Background / label / border for one quick action button. Outline buttons drop
 * the fill and take the accent as both rule and label; solid buttons keep a
 * same-coloured border so the two treatments occupy identical space.
 */
const actionTriplet = (
  color: string,
  p: DashboardPalette,
  style: DashboardStyle,
) =>
  style.buttonStyle === "outline"
    ? { bg: "transparent", fg: color, bd: color }
    : { bg: color, fg: p.actionText, bd: color };

/** Inline style for an amount / ticket-type tile. */
export const tileSurface = (
  preset: DashboardPreset,
  bg: string,
  fg: string,
): CSSProperties =>
  preset.style.tileStyle === "outline"
    ? {
        background: "transparent",
        border: `${preset.style.borderWidth}px solid ${fg}`,
        color: fg,
      }
    : {
        background: bg,
        border: `${preset.style.borderWidth}px solid transparent`,
        color: fg,
      };

/**
 * CSS custom properties for the dashboard root. Widgets read these instead of
 * hard-coded Tailwind colour classes, so switching preset repaints instantly.
 */
export const dashboardThemeVars = (
  preset: DashboardPreset,
  mode: "light" | "dark",
): CSSProperties => {
  const p = paletteFor(preset, mode);
  const s = preset.style;
  const shadow = s.glow
    ? `0 0 14px ${s.glow}40`
    : SHADOWS[Math.min(Math.max(preset.shadow, 0), 3)];

  const emerald = actionTriplet(p.actionEmerald, p, s);
  const blue = actionTriplet(p.actionBlue, p, s);
  const red = actionTriplet(p.actionRed, p, s);
  const outlined = s.tileStyle === "outline";

  return {
    "--dash-page-bg": p.pageBg,
    "--dash-panel-fill": panelFillValue(preset, p),
    /* Solid equivalents for app chrome (sidebar, headers, antd surfaces),
       which need a real colour even when dashboard panels are unfilled. */
    "--dash-surface": p.panelBg,
    "--dash-surface-alt": p.panelBgAlt,
    "--dash-panel-border": p.panelBorder,
    "--dash-border-width": `${s.borderWidth}px`,
    "--dash-heading": p.heading,
    "--dash-heading-weight": `${s.headingWeight}`,
    "--dash-muted": p.muted,
    "--dash-value": p.value,
    "--dash-link": ensureContrast(
      p.actionEmerald,
      s.panelFill === "none" ? p.pageBg : p.panelBg,
    ),
    "--dash-label-case": s.labelCase === "upper" ? "uppercase" : "none",
    "--dash-label-tracking": `${s.labelTracking}em`,
    "--dash-numeric-font": s.numericFont === "mono" ? MONO_STACK : "inherit",
    "--dash-icon-bg": outlined ? "transparent" : p.iconBg,
    "--dash-icon-text": p.iconText,
    "--dash-icon-border": outlined ? p.iconText : "transparent",
    "--dash-icon-bg-down": outlined ? "transparent" : p.iconBgDown,
    "--dash-icon-text-down": p.iconTextDown,
    "--dash-icon-border-down": outlined ? p.iconTextDown : "transparent",
    "--dash-action-emerald-bg": emerald.bg,
    "--dash-action-emerald-fg": emerald.fg,
    "--dash-action-emerald-bd": emerald.bd,
    "--dash-action-blue-bg": blue.bg,
    "--dash-action-blue-fg": blue.fg,
    "--dash-action-blue-bd": blue.bd,
    "--dash-action-red-bg": red.bg,
    "--dash-action-red-fg": red.fg,
    "--dash-action-red-bd": red.bd,
    /** icon pill inside a button — invisible on outline buttons */
    "--dash-action-chip":
      s.buttonStyle === "outline" ? "transparent" : "rgba(0, 0, 0, 0.15)",
    "--dash-radius": `${preset.radius}px`,
    "--dash-gap": `${preset.gap}px`,
    "--dash-shadow": shadow,
    "--dash-font-scale": `${preset.fontScale}`,
  } as CSSProperties;
};

/* --------------------------- app-wide plumbing --------------------------- */

const channel = (hexPair: string) => parseInt(hexPair, 16) / 255;
const toLinear = (c: number) =>
  c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);

/** WCAG relative luminance. Accepts #rgb or #rrggbb. */
export const luminance = (hex: string): number => {
  let h = hex.replace("#", "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  const [r, g, b] = [h.slice(0, 2), h.slice(2, 4), h.slice(4, 6)]
    .map(channel)
    .map(toLinear);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/**
 * Whether a palette reads as dark. Drives antd's light/dark algorithm, so a
 * preset like Mono or Midnight themes the whole app dark even while the app's
 * own light/dark toggle says light.
 */
export const isDarkPalette = (p: DashboardPalette): boolean =>
  luminance(p.pageBg) < 0.2;

/** Contrast ratio between two hex colours. */
export const contrastRatio = (a: string, b: string): number => {
  const l1 = luminance(a);
  const l2 = luminance(b);
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
};

const toRgb = (hex: string): [number, number, number] => {
  let h = hex.replace("#", "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
};

const toHex = (r: number, g: number, b: number) =>
  "#" +
  [r, g, b]
    .map((v) =>
      Math.round(Math.min(255, Math.max(0, v)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("");

/** Move a colour toward white or black until it is legible on `bg`. */
export const ensureContrast = (
  fg: string,
  bg: string,
  target = 4.5,
): string => {
  if (contrastRatio(fg, bg) >= target) return fg;
  const towardWhite = luminance(bg) < 0.5;
  const [r, g, b] = toRgb(fg);
  for (let i = 1; i <= 20; i += 1) {
    const t = i / 20;
    const mixed = towardWhite
      ? toHex(r + (255 - r) * t, g + (255 - g) * t, b + (255 - b) * t)
      : toHex(r * (1 - t), g * (1 - t), b * (1 - t));
    if (contrastRatio(mixed, bg) >= target) return mixed;
  }
  return towardWhite ? "#ffffff" : "#000000";
};

/**
 * antd design tokens for a preset, so Tables, Modals, Drawers, Selects and the
 * rest of the component library follow the store's theme instead of sitting on
 * default white.
 */
export const antdTokensFor = (
  preset: DashboardPreset,
  mode: "light" | "dark",
) => {
  const p = paletteFor(preset, mode);
  const surface = preset.style.panelFill === "none" ? p.pageBg : p.panelBg;
  return {
    colorPrimary: p.actionEmerald,
    colorLink: ensureContrast(p.actionEmerald, surface),
    colorLinkHover: ensureContrast(p.actionEmerald, surface, 7),
    colorInfo: p.actionBlue,
    colorError: p.actionRed,
    colorSuccess: p.actionEmerald,
    colorBgBase: p.panelBg,
    colorTextBase: p.heading,
    colorBgContainer: p.panelBg,
    colorBgElevated: p.panelBgAlt,
    colorBgLayout: p.pageBg,
    colorText: p.heading,
    colorTextSecondary: p.muted,
    colorTextDescription: p.muted,
    colorBorder: p.panelBorder,
    colorBorderSecondary: p.panelBorder,
    borderRadius: Math.min(preset.radius, 12),
  };
};
